import { useEffect, useState } from 'react'
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux'
import { failWithErrorResponse, success } from 'features/toast/Toast'
import {
  BiddingStrategyTypes,
  ChangeAmountUnit,
  getRankOptionSimpleText,
  getRankOptionText,
  MaxBiddingAmountChangeTypes,
  RankOptions,
} from 'features/Codes'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import useValidator from 'utils/hooks/useValidator'
import SelectBox from 'components/form/SelectBox'
import CurrencyInput from 'components/form/CurrencyInput'
import RadioButton from 'components/form/RadioButton'
import ErrorText from 'components/form/ErrorText'
import {
  boundary,
  decreaseByPercent,
  increaseByPercent,
  integerValue,
  invalidNumberInput,
  isEmptyObject,
  isNullOrUndefined,
} from 'utils'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import Table from 'components/table/Table'
import {
  editKeywordBiddingStrategy,
  fetchBiddingAverages,
  hideKeywordBiddingStrategyEdit,
  hidePreview,
  setBiddingStrategyType,
  setChangeAmountUnit,
  setMaxBiddingAmountChangeType,
  setRank,
  setUseRankAmount,
  showPreview,
} from 'features/dashboard/operation/keywordBiddingStrategyEdit/keywordBiddingStrategyEditModalSlice'
import CheckBox from 'components/form/CheckBox'
import classNames from 'classnames'
import { ceil, floor } from 'lodash'
import { Modal } from 'components/dialog/Modals'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import { setSelectedIds } from 'features/dashboard/table/keyword/keywordTableSlice'
import FixedTable from 'components/table/FixedTable'

const KeywordBiddingStrategyEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    keywords: selectedKeywords,
    form: {
      changeAmount,
      maxBiddingAmount,
      maxBiddingAmountChangeType,
      biddingStrategyType,
      changeAmountUnit,
      rank,
      useRankAmount,
      rankAmount,
    },
    preview: {
      changeAmount: previewChangeAmount,
      maxBiddingAmount: previewMaxBiddingAmount,
      maxBiddingAmountChangeType: previewMaxBiddingAmountChangeType,
      biddingStrategyType: previewBiddingStrategyType,
      changeAmountUnit: previewChangeAmountUnit,
      rank: previewRank,
      useRankAmount: previewUseRankAmount,
      rankAmount: previewRankAmount,
    },
    previewVisible,
    biddingAverageLoading,
    isSubmitting,
    biddingAverages,
    biddingAverageLoaded,
  } = useSelector(
    state => state.dashboard.operation.keywordBiddingStrategyEditModal,
    shallowEqual
  )
  const [changeAmountInput, setChangeAmountInput] = useState(changeAmount)
  const [maxBiddingAmountInput, setMaxBiddingAmountInput] =
    useState(maxBiddingAmount)
  const [rankAmountInput, setRankAmountInput] = useState(rankAmount)
  useEffect(() => {
    if (visible) {
      setChangeAmountInput(changeAmount)
      setMaxBiddingAmountInput(maxBiddingAmount)
      setRankAmountInput(rankAmount)
    }
  }, [visible, changeAmount, maxBiddingAmount, rankAmount])
  const isCalculateType = [
    MaxBiddingAmountChangeTypes.INCREASE,
    MaxBiddingAmountChangeTypes.DECREASE,
  ].includes(maxBiddingAmountChangeType)
  const isCalculateTypePreview = [
    MaxBiddingAmountChangeTypes.INCREASE,
    MaxBiddingAmountChangeTypes.DECREASE,
  ].includes(previewMaxBiddingAmountChangeType)
  const { validate, errors } = useValidator(() => {
    if (biddingStrategyType === BiddingStrategyTypes.INHERIT_FROM_ADGROUP)
      return {}
    return {
      maxBiddingAmount:
        maxBiddingAmountChangeType ===
          MaxBiddingAmountChangeTypes.NO_CALCULATE &&
        invalidNumberInput(maxBiddingAmountInput, 100000, 70, 10) &&
        '입찰가는 최소 70원, 최대 100,000원 범위 내에서 10원 단위로 등록 가능합니다.',
      changeAmount:
        (isCalculateType &&
          changeAmountInput === '' &&
          '증감 범위를 등록하세요') ||
        (isCalculateType &&
          changeAmountUnit === ChangeAmountUnit.WON &&
          changeAmountInput % 10 !== 0 &&
          '10원 단위로 등록 가능합니다.'),
      rankAmount:
        maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK &&
        useRankAmount &&
        invalidNumberInput(rankAmountInput, 100000, 70, 10) &&
        '최대 입찰가는 최소 70원, 최대 100,000원 범위 내에서 10원 단위로 등록 가능합니다.',
    }
  }, [
    biddingStrategyType,
    maxBiddingAmountChangeType,
    maxBiddingAmountInput,
    isCalculateType,
    changeAmountUnit,
    changeAmountInput,
    useRankAmount,
    rankAmountInput,
  ])

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideKeywordBiddingStrategyEdit())
    },
    [dispatch]
  )

  const calculate = (before, adGroupMaxBiddingAmount, text) => {
    let after
    if (
      previewBiddingStrategyType === BiddingStrategyTypes.INHERIT_FROM_ADGROUP
    ) {
      after = adGroupMaxBiddingAmount
    } else if (
      previewMaxBiddingAmountChangeType ===
      MaxBiddingAmountChangeTypes.NO_CALCULATE
    ) {
      after = previewMaxBiddingAmount
    } else if (isCalculateTypePreview) {
      const calculateAmount =
        previewChangeAmountUnit === ChangeAmountUnit.PERCENTAGE
          ? previewMaxBiddingAmountChangeType ===
            MaxBiddingAmountChangeTypes.INCREASE
            ? increaseByPercent(before, Number(previewChangeAmount))
            : decreaseByPercent(before, Number(previewChangeAmount))
          : previewMaxBiddingAmountChangeType ===
            MaxBiddingAmountChangeTypes.INCREASE
          ? ceil(before + Number(previewChangeAmount), -1)
          : floor(before - Number(previewChangeAmount), -1)
      after = boundary(calculateAmount, 100000, 70)
    } else if (
      previewMaxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK
    ) {
      const biddingAverageAmount = biddingAverages
        ?.find(({ keyword }) => keyword === text)
        ?.[previewRank.device.toLowerCase()]?.find(
          item => item.rank === previewRank.rank
        )?.averageBiddingAmount
      const exceedRankAmount =
        previewUseRankAmount && biddingAverageAmount > previewRankAmount
      after = biddingAverageAmount
        ? exceedRankAmount
          ? Math.min(previewRankAmount, 100000)
          : Math.min(biddingAverageAmount, 100000)
        : 70
    }
    return {
      after,
      percentage:
        isNullOrUndefined(before) || isNullOrUndefined(after)
          ? 0
          : (after / before) * 100,
    }
  }

  const warningCount = selectedKeywords.filter(
    ({ text, biddingStrategy, adGroupMaxBiddingAmount }) =>
      calculate(
        biddingStrategy.maxBiddingAmount || adGroupMaxBiddingAmount,
        adGroupMaxBiddingAmount,
        text
      ).percentage >= 500
  ).length

  const createCommand = () =>
    selectedKeywords.map(
      ({ id, text, biddingStrategy, adGroupMaxBiddingAmount }) => ({
        keywordId: id,
        biddingStrategyType,
        ...(biddingStrategyType !==
          BiddingStrategyTypes.INHERIT_FROM_ADGROUP && {
          maxBiddingAmountChangeType: MaxBiddingAmountChangeTypes.NO_CALCULATE,
        }),
        ...(maxBiddingAmountChangeType ===
        MaxBiddingAmountChangeTypes.NO_CALCULATE
          ? { maxBiddingAmount: maxBiddingAmountInput }
          : {
              maxBiddingAmount: calculate(
                biddingStrategy.maxBiddingAmount || adGroupMaxBiddingAmount,
                adGroupMaxBiddingAmount,
                text
              ).after,
            }),
      })
    )

  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(
          showPreview({
            maxBiddingAmount: maxBiddingAmountInput,
            changeAmount: changeAmountInput,
            rankAmount: rankAmountInput,
          })
        )
        if (
          !biddingAverageLoaded &&
          maxBiddingAmountChangeType === MaxBiddingAmountChangeTypes.RANK
        ) {
          const keywords = selectedKeywords.map(({ text }) => text)
          const { error, payload } = await dispatch(
            fetchBiddingAverages(keywords)
          )
          if (error) {
            failWithErrorResponse(payload)
          }
        }
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const commands = createCommand()
        const { error, payload } = await dispatch(
          editKeywordBiddingStrategy(commands)
        )
        if (error) {
          preventClose()
          failWithErrorResponse(payload)
        } else {
          success('키워드 변경사항이 적용되었습니다.')
          batch(() => {
            dispatch(setSelectedIds([]))
            dispatch(refreshDashboard())
          })
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    changeAmountInput !== previewChangeAmount ||
    maxBiddingAmountInput !== previewMaxBiddingAmount ||
    maxBiddingAmountChangeType !== previewMaxBiddingAmountChangeType ||
    biddingStrategyType !== previewBiddingStrategyType ||
    changeAmountUnit !== previewChangeAmountUnit ||
    rank !== previewRank ||
    useRankAmount !== previewUseRankAmount ||
    rankAmountInput !== previewRankAmount

  return (
    <Modal on={visible} className="bidding_layer">
      <PreviewConfirmModal
        title="키워드 입찰가 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={(!changed && preview) || biddingAverageLoading}
        confirmDisabled={changed || !preview || biddingAverageLoading}
        handleClose={() => dispatch(hideKeywordBiddingStrategyEdit())}>
        <p className="desc_info">
          키워드의 입찰가가 설정된 경우 광고그룹의 기본입찰가 대신 키워드의
          기본입찰가를 사용합니다. 단 입찰 가중치는 광고그룹에 설정된 값을
          사용합니다. 콘텐츠 매체의 경우 키워드의 입찰가를 사용하지 않으며,
          콘텐츠 매체 전용 입찰가를 사용합니다.
        </p>

        <div className="group_inp">
          <RadioButton
            label="고정입찰"
            checkValue={biddingStrategyType}
            value={BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING}
            tiaraAction="키워드탭_입찰가변경_일괄_고정입찰_클릭"
            onClick={() => {
              dispatch(
                setBiddingStrategyType(
                  BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING
                )
              )
              setMaxBiddingAmountInput('')
              setChangeAmountInput('')
              setRankAmountInput('')
            }}
          />
          <RadioButton
            label="광고그룹의 기본 입찰가로 변경"
            checkValue={biddingStrategyType}
            value={BiddingStrategyTypes.INHERIT_FROM_ADGROUP}
            tiaraAction="키워드탭_입찰가변경_일괄_기본입찰가_클릭"
            onClick={() => {
              dispatch(
                setBiddingStrategyType(
                  BiddingStrategyTypes.INHERIT_FROM_ADGROUP
                )
              )
              setMaxBiddingAmountInput('')
              setChangeAmountInput('')
              setRankAmountInput('')
            }}
          />
        </div>
        {biddingStrategyType ===
          BiddingStrategyTypes.FIXBIDDING_BY_CUSTOM_MAX_BIDDING && (
          <div className="group_bidding">
            <div className="item_bidding">
              <div className="f_left">
                <div className="single_wrap">
                  <SelectBox
                    value={maxBiddingAmountChangeType}
                    onValueChange={v => {
                      dispatch(setMaxBiddingAmountChangeType(v))
                      setMaxBiddingAmountInput('')
                      setChangeAmountInput('')
                      setRankAmountInput('')
                    }}
                    noBlurOnDefault
                    disabled={
                      biddingStrategyType ===
                      BiddingStrategyTypes.INHERIT_FROM_ADGROUP
                    }
                    className="opt_cate">
                    <SelectBox.Item
                      value={MaxBiddingAmountChangeTypes.NO_CALCULATE}
                      tiaraAction="키워드탭_입찰가변경_일괄_직접입력_클릭">
                      직접입력
                    </SelectBox.Item>
                    <SelectBox.Item
                      value={MaxBiddingAmountChangeTypes.INCREASE}
                      tiaraAction="키워드탭_입찰가변경_일괄_입찰가증액_클릭">
                      입찰가 증액
                      <HelpTooltip nowrap={false} tag="span">
                        증액/감액으로 계산된 입찰가 금액은 최소 70원 ~ 최대
                        100,000원 이내로 설정됩니다.
                      </HelpTooltip>
                    </SelectBox.Item>
                    <SelectBox.Item
                      value={MaxBiddingAmountChangeTypes.DECREASE}
                      tiaraAction="키워드탭_입찰가변경_일괄_입찰가감액_클릭">
                      입찰가 감액
                      <HelpTooltip nowrap={false} tag="span">
                        증액/감액으로 계산된 입찰가 금액은 최소 70원 ~ 최대
                        100,000원 이내로 설정됩니다.
                      </HelpTooltip>
                    </SelectBox.Item>
                    <SelectBox.Item
                      value={MaxBiddingAmountChangeTypes.RANK}
                      tiaraAction="키워드탭_입찰가변경_일괄_순위별평균_클릭">
                      순위별 평균 입찰가
                      <HelpTooltip nowrap={false} tag="span">
                        순위별 평균 입찰가는 프리미엄링크 캠페인 &gt; 검색매체
                        &gt; 카카오지면을 통해 광고가 집행된 키워드의 최근
                        30일(어제로부터 과거 30일까지) 과거 실적을 토대로 예측한
                        수치며 제공되는 데이터로 인한 광고 성과를 보장하지
                        않습니다. 순위별 평균 입찰가가 희망하는 입찰가를
                        초과하지 않도록 최대 입찰가를 설정할 수 있습니다.
                      </HelpTooltip>
                    </SelectBox.Item>
                  </SelectBox>
                </div>
                <div className="single_wrap">
                  {maxBiddingAmountChangeType ===
                    MaxBiddingAmountChangeTypes.NO_CALCULATE && (
                    <CurrencyInput
                      value={maxBiddingAmountInput}
                      min={70}
                      max={100000}
                      step={10}
                      maxLength={6}
                      error={errors.maxBiddingAmount}
                      onValueChange={v =>
                        setMaxBiddingAmountInput(integerValue(v))
                      }
                    />
                  )}
                  {isCalculateType && (
                    <>
                      <SelectBox
                        className="opt_count"
                        value={changeAmountUnit}
                        onValueChange={v => {
                          dispatch(setChangeAmountUnit(v))
                          setChangeAmountInput('')
                        }}
                        noBlurOnDefault
                        disabled={
                          biddingStrategyType ===
                          BiddingStrategyTypes.INHERIT_FROM_ADGROUP
                        }>
                        <SelectBox.Item value={ChangeAmountUnit.WON}>
                          원
                        </SelectBox.Item>
                        <SelectBox.Item value={ChangeAmountUnit.PERCENTAGE}>
                          %
                        </SelectBox.Item>
                      </SelectBox>
                      {changeAmountUnit === ChangeAmountUnit.PERCENTAGE ? (
                        <CurrencyInput
                          type="%"
                          value={changeAmountInput}
                          min={0}
                          max={
                            maxBiddingAmountChangeType ===
                            MaxBiddingAmountChangeTypes.DECREASE
                              ? 100
                              : 999
                          }
                          step={1}
                          maxLength={3}
                          error={errors.changeAmount}
                          onValueChange={v =>
                            setChangeAmountInput(integerValue(v))
                          }
                        />
                      ) : (
                        <CurrencyInput
                          value={changeAmountInput}
                          max={100000}
                          step={10}
                          maxLength={6}
                          error={errors.changeAmount}
                          onValueChange={v =>
                            setChangeAmountInput(integerValue(v))
                          }
                        />
                      )}
                    </>
                  )}
                  {maxBiddingAmountChangeType ===
                    MaxBiddingAmountChangeTypes.RANK && (
                    <SelectBox
                      className="opt_ranking"
                      noBlurOnDefault
                      value={rank}
                      onValueChange={v => dispatch(setRank(v))}>
                      {RankOptions.map(item => (
                        <SelectBox.Item
                          key={`${item.device}${item.rank}`}
                          value={item}
                          tiaraAction={`키워드탭_입찰가변경_일괄_순위별평균_${getRankOptionSimpleText(
                            item
                          )}위_클릭`}>
                          {getRankOptionText(item)}
                        </SelectBox.Item>
                      ))}
                    </SelectBox>
                  )}
                </div>
              </div>
            </div>
            {maxBiddingAmountChangeType ===
              MaxBiddingAmountChangeTypes.RANK && (
              <div className="box_info">
                <div className="inner_info">
                  <CheckBox
                    label="최대입찰가"
                    checked={useRankAmount}
                    onCheckedChange={v => dispatch(setUseRankAmount(v))}
                  />
                  <CurrencyInput
                    disabled={!useRankAmount}
                    min={70}
                    max={100000}
                    step={10}
                    maxLength={6}
                    error={errors.rankAmount}
                    value={rankAmountInput}
                    onValueChange={v => setRankAmountInput(integerValue(v))}
                  />
                </div>
              </div>
            )}
            <ErrorText
              tag="p"
              error={
                errors.rankAmount ||
                errors.maxBiddingAmount ||
                errors.changeAmount
              }
            />
          </div>
        )}
        {preview && (
          <>
            <FixedTable
              caption="일예산 변경 이력 표"
              loading={biddingAverageLoading}>
              <Table.Head>
                <Table.Column
                  title="선택키워드"
                  className="inner_cell"
                  width={194}
                />
                <Table.Column
                  title="변경 전"
                  className="inner_cell"
                  width={171}
                />
                <Table.Column title="변경 후" className="inner_cell" />
              </Table.Head>
              <Table.Body>
                {selectedKeywords.map(keyword => {
                  const { id, text, biddingStrategy, adGroupMaxBiddingAmount } =
                    keyword
                  const isDefault = isNullOrUndefined(
                    keyword.biddingStrategy.maxBiddingAmount
                  )
                  const setDefault =
                    previewBiddingStrategyType ===
                    BiddingStrategyTypes.INHERIT_FROM_ADGROUP
                  const before =
                    biddingStrategy.maxBiddingAmount || adGroupMaxBiddingAmount
                  const { after, percentage } = calculate(
                    before,
                    adGroupMaxBiddingAmount,
                    text
                  )
                  const fixed = Math.ceil(percentage.toFixed(1))
                  return (
                    <Table.Row
                      rowId={id}
                      key={id}
                      className={classNames({
                        update: before !== after || isDefault !== setDefault,
                      })}>
                      <td>
                        <div className="inner_cell">
                          <span className="txt_limit2">{text}</span>
                        </div>
                      </td>
                      <td>
                        <div className="inner_cell">
                          {`${
                            isDefault ? '[기본] ' : ''
                          }${before.toLocaleString()}원`}
                        </div>
                      </td>
                      <td>
                        <div className="inner_cell">
                          {`${
                            setDefault ? '[기본] ' : ''
                          }${after.toLocaleString()}원`}
                          {percentage >= 500 && (
                            <span className="txt_error">
                              {fixed.toLocaleString()}%로 증가폭이 큽니다.
                            </span>
                          )}
                        </div>
                      </td>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </FixedTable>
            {warningCount > 0 && (
              <p className="txt_error">
                변경 전 금액 대비 증가폭이 큰 입력값이 {warningCount}개
                존재합니다. 입력값이 맞는지 확인하세요.
              </p>
            )}
          </>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}

export default KeywordBiddingStrategyEditModal
